import React, { useMemo, useEffect } from 'react';
import SelectSearch from 'react-select-search';
import { connect } from 'react-redux';

import { fetchCouncils } from 'actions/councils';
import { updateOrder, fetchAddressSuggestions } from 'actions/orders';

import Card from 'components/common/Card';
import RadioButton from 'components/RadioButton';
import LoadingSpinner from 'components/LoadingSpinner';

import {
  StepTitle,
  StepSubTitle,
  LoadingContain,
  ContentContain,
  StepInformation
} from '../CreateStyled';

import { stepCouncil as handleValidate } from '../validation/';

import { getUniqueLGAs, getCouncilByLga } from 'utils';
import { addressDisplaySelector } from 'selectors/order-create';

export const StepCouncil = ({
  loading,
  councils,
  addresses,
  updateOrder,
  fetchCouncils,
  renderActions,
  customCouncil,
  addressDisplay,
  loadingCouncils,
  selectedCouncil,
  fetchCouncilSuggestions
}) => {
  useEffect(() => {
    fetchCouncils();
    fetchCouncilSuggestions(addressDisplay, true);
  }, [fetchCouncilSuggestions, addressDisplay, fetchCouncils]);

  const suggestions = useMemo(
    function getCouncilSuggestions() {
      const mapFriendlyLga = (m, lga) => {
        const match = getCouncilByLga(councils, lga);
        return match ? [...m, match.lga] : m;
      };

      return getUniqueLGAs(addresses).reduce(mapFriendlyLga, []);
    },
    [councils, addresses]
  );

  const handleNext = () => {
    return handleValidate(selectedCouncil);
  };

  const handleCouncilChange = (lga) => {
    updateOrder({
      selected_council: lga,
      selected_council_other: false,
      selected_council_id: getCouncilByLga(councils, lga).id
    });
  };

  const handleCustomCouncilChange = (lga, id) => {
    updateOrder({
      selected_council: lga,
      selected_council_id: id,
      selected_council_other: true
    });
  };

  const renderSuggestions = () =>
    suggestions.map((lga) => (
      <RadioButton
        key={lga}
        name={lga}
        label={lga}
        onClick={handleCouncilChange.bind(null, lga)}
        checked={!customCouncil && selectedCouncil === lga}
      />
    ));

  const mapCouncilOptions = () =>
    councils
      .slice()
      .sort((a, b) => {
        if (a.lga > b.lga) return 1;
        if (a.lga < b.lga) return -1;
        return 0;
      })
      .map((council) => ({ council, name: council.lga, value: council.lga }));

  return (
    <Card.Content>
      <ContentContain>
        <StepTitle>Select Council</StepTitle>
        <StepInformation>
          {`Please confirm your preferred council for ${addressDisplay}`}
        </StepInformation>

        <StepSubTitle>Suggested Council(s)</StepSubTitle>

        {loading || loadingCouncils ? (
          <LoadingContain>
            <LoadingSpinner large />
            Loading Council Matches...
          </LoadingContain>
        ) : suggestions.length ? (
          renderSuggestions()
        ) : (
          <StepInformation>
            No council suggestions were found. Please select your preferred council
            below.
          </StepInformation>
        )}

        <StepSubTitle>All Councils</StepSubTitle>

        {loadingCouncils ? (
          <LoadingContain>
            <LoadingSpinner large />
            Loading Councils...
          </LoadingContain>
        ) : (
          <SelectSearch
            value={selectedCouncil}
            name="council-select"
            placeholder="Select / Search Council"
            options={mapCouncilOptions()}
            onChange={({ value, council }) =>
              handleCustomCouncilChange(value, council.id)
            }
          />
        )}

        {renderActions({ disableNext: !selectedCouncil, onNext: handleNext })}
      </ContentContain>
    </Card.Content>
  );
};

StepCouncil.defaultProps = {
  inputErrors: {},
  onValueChange: () => null
};

export default connect(
  (state) => ({
    councils: state.councils.items,
    loading: state.loading.loadingAddresses,
    loadingCouncils: state.loading.loadingCouncils,
    addressDisplay: addressDisplaySelector(state),
    addresses: state.pendingOrder.pending.council_addresses,
    selectedCouncil: state.pendingOrder.pending.selected_council
  }),
  {
    dispatchOrderUpdate: updateOrder,
    dispatchGetCouncils: fetchCouncils,
    dispatchGetSuggestions: fetchAddressSuggestions
  }
  )(props =>
  <StepCouncil
    {...props}
    updateOrder={props.dispatchOrderUpdate}
    fetchCouncils={props.dispatchGetCouncils}
    fetchCouncilSuggestions={props.dispatchGetSuggestions}
  />
);

